import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileStack, ShieldCheck, Boxes, Users, CalendarDays, Sprout, FileDown, ScanLine, ArrowRight, Check, Gift } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api } from "../lib/api";
import { money, run } from "../lib/util";
import { Btn, Field, Input, Select } from "../components/kit";

export const Logo = ({ light }) => (
  <div className="flex items-center gap-2.5 relative z-10" data-testid="brand-logo">
    <div className="w-9 h-9 rounded-lg bg-[#FF6500] grid place-items-center font-heading font-extrabold text-white text-lg">M</div>
    <div className="leading-none">
      <div className={`font-heading font-extrabold text-xl ${light ? "text-white" : "text-[#0B192C]"}`}>Map<span className="text-[#FF6500]">IA</span></div>
      <div className={`text-[10px] uppercase tracking-[0.18em] ${light ? "text-slate-400" : "text-slate-500"}`}>WebFast Soluções Inteligentes</div>
    </div>
  </div>
);

const BENEFITS = [
  [ScanLine, "Leitura de documentos por IA", "Notas fiscais em PDF, imagem ou XML de NF-e, cardápios, contratos e fichas técnicas lidos automaticamente, sempre com conferência antes de alterar o estoque."],
  [ShieldCheck, "Bloqueio de duplicidade", "Hash do arquivo, chave de acesso e fornecedor + número + série impedem que a mesma nota entre duas vezes."],
  [Boxes, "Estoque contínuo", "O saldo final de um mês vira o saldo inicial do próximo. Edições recalculam os meses seguintes."],
  [Users, "Comensais por turno", "Calculados a partir dos alunos matriculados de cada turno, com variação diária entre as refeições."],
  [CalendarDays, "Calendário escolar", "Feriados, pontos facultativos, suspensões e sábados de reposição cadastrados pela escola."],
  [Sprout, "Agricultura familiar", "Controle do contratado, comprado e a comprar em cada contrato, com alertas de vigência e excesso."],
];

const FEATURES = [
  "Menu com os 6 documentos do mês: mapa anterior, cardápio SEEDUC, NFs do fornecedor principal, contrato e NFs da agricultura familiar e fichas técnicas",
  "Envio de vários arquivos de uma vez em todos os menus, com progresso e resultado por arquivo",
  "Modo IA: distribuição diária dos gêneros das notas pelo per capita das fichas técnicas × comensais de cada turno",
  "Substituições automáticas por grupo alimentar quando o cardápio não corresponde ao que foi comprado",
  "Distribuição por preparo visível e editável antes de gerar o mapa, com ajuste de sobras opcional",
  "Modo IA e modo Retiradas, claramente identificados nos totais",
  "Formulários: cardápio diário, controle diário de gêneros e controle mensal de estoque no layout oficial em PDF",
  "Conclusão do mês com PDF do mapa completo guardado para download",
  "Multi escolas com isolamento total de dados entre unidades",
  "Histórico de alterações e controle de versão contra sobrescritas",
];

const FAQ = [
  ["Os dados de uma escola ficam visíveis para outra?", "Não. Cada gestor acessa apenas a própria escola. O isolamento é aplicado no servidor, no banco de dados e no armazenamento dos arquivos."],
  ["A IA altera o estoque sozinha?", "Não. Toda leitura passa por uma tela de conferência. Dados não identificados ficam marcados como pendentes para preenchimento manual."],
  ["Posso enviar a mesma nota em PDF e em XML?", "O sistema identifica a mesma NF-e pela chave de acesso e bloqueia o segundo lançamento. Quando não há identificação conclusiva, solicita conferência."],
  ["Preciso importar o mapa anterior todo mês?", "Não. A importação do mapa anterior é feita apenas na implantação. Depois, o saldo final de cada mês segue automaticamente para o próximo."],
  ["O Modo IA comprova consumo?", "Não. O Modo IA é um planejamento da distribuição. Para registrar o que saiu do estoque, use o modo Retiradas."],
  ["Como contrato um plano?", "Clique em “Conhecer”, preencha seus dados e a equipe da WebFast entrará em contato para ativar a escola."],
];

const PRICES = [
  ["gestor", "O gestor faz o preenchimento", 89.9, ["Você envia os documentos e confere cada etapa", "Mapa completo em PDF e Word", "Suporte pelo WhatsApp"]],
  ["equipe", "A equipe MapIA faz para você", 149.9, ["Nossa equipe faz o preenchimento do mês", "Mapa completo em PDF e Word", "Você só confere e entrega"]],
];

export function OfferBanner({ offer, onHire, compact }) {
  if (!offer?.active) return null;
  return (
    <div data-testid="offer-banner" className={`rounded-2xl border border-orange-200 bg-gradient-to-r from-[#FFF3EB] to-white flex flex-col sm:flex-row sm:items-center gap-3 justify-between ${compact ? "p-3 text-sm" : "p-5 sm:p-6"}`}>
      <div className="flex gap-3 items-start">
        <Gift className="w-6 h-6 text-[#FF6500] shrink-0" />
        <div>
          <div className="font-heading font-bold text-[#0B192C]">Oferta de lançamento</div>
          <div className="text-slate-600">O primeiro mapa de merenda é grátis para as 100 primeiras escolas, quando o gestor faz o preenchimento sozinho.</div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span data-testid="offer-remaining" className="rounded-full bg-[#FF6500] text-white px-3 py-1 text-sm font-bold whitespace-nowrap">Restam {offer.remaining} de {offer.total} vagas</span>
        {onHire && <Btn data-testid="offer-hire-button" className="!rounded-full" onClick={() => onHire("gestor")}>Quero participar</Btn>}
      </div>
    </div>
  );
}

function LeadDialog({ offer, filling, onClose }) {
  const [f, setF] = useState({ name: "", school: "", email: "", whatsapp: "", municipio: "", filling: filling || "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/public/leads", { ...f, filling: f.filling || null }))) setSent(true);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Conhecer o MapIA</DialogTitle></DialogHeader>
        <OfferBanner offer={offer} compact />
        {sent ? (
          <div data-testid="lead-success" className="text-sm text-slate-600 py-4">Solicitação enviada. A equipe da WebFast entrará em contato pelo e-mail ou WhatsApp informado.</div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label="Seu nome" className="col-span-2"><Input data-testid="lead-name-input" required value={f.name} onChange={set("name")} /></Field>
            <Field label="Escola" className="col-span-2"><Input data-testid="lead-school-input" required value={f.school} onChange={set("school")} /></Field>
            <Field label="E-mail"><Input data-testid="lead-email-input" type="email" required value={f.email} onChange={set("email")} /></Field>
            <Field label="WhatsApp"><Input data-testid="lead-whatsapp-input" value={f.whatsapp} onChange={set("whatsapp")} /></Field>
            <Field label="Município"><Input data-testid="lead-municipio-input" value={f.municipio} onChange={set("municipio")} /></Field>
            <Field label="Preenchimento" className="col-span-2"><Select data-testid="lead-filling-select" value={f.filling} onChange={set("filling")} placeholder="A definir" options={PRICES.map(([k, t, p]) => [k, `${t} (${money(p)})`])} /></Field>
            <Field label="Mensagem" className="col-span-2"><textarea data-testid="lead-message-input" className="w-full rounded-lg border border-slate-300 p-2 text-sm" rows={3} value={f.message} onChange={set("message")} /></Field>
            <Btn data-testid="lead-submit-button" className="col-span-2">Enviar solicitação</Btn>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Plans({ onHire }) {
  return (
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
      {PRICES.map(([k, t, p, items], i) => (
        <div key={k} data-testid={`plan-card-${i}`} className={`rounded-2xl p-7 border flex flex-col ${i === 1 ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white border-slate-200"}`}>
          <h3 className="font-heading text-xl font-bold">{t}</h3>
          <div className="mt-5 font-heading text-3xl font-extrabold">{money(p)}<span className="text-sm font-medium opacity-60"> / mapa</span></div>
          <ul className="mt-5 space-y-2 text-sm flex-1">
            {items.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-[#FF6500] shrink-0 mt-0.5" />{f}</li>)}
          </ul>
          <Btn data-testid={`plan-hire-${i}`} className="mt-6" onClick={() => onHire(k)}>Conhecer</Btn>
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const [offer, setOffer] = useState(null);
  const [lead, setLead] = useState(null);
  useEffect(() => {
    api.get("/public/offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-[#F8FAFC] overflow-x-hidden -mb-20">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 h-16 flex items-center justify-between gap-3">
          <Logo />
          <nav className="hidden md:flex items-center gap-1 text-sm font-medium text-slate-600">
            {[["#beneficios", "Benefícios"], ["#funcionalidades", "Funcionalidades"], ["#planos", "Planos"], ["#faq", "Perguntas"]].map(([h, l]) => (
              <a key={h} href={h} data-testid={`nav-${h.slice(1)}`} className="px-3 py-2 rounded-full hover:bg-orange-50 hover:text-[#FF6500] transition-colors">{l}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Btn variant="outline" data-testid="header-access-button" className="!rounded-full px-4">Acessar</Btn></Link>
            <Btn data-testid="header-hire-button" className="hidden sm:inline-flex !rounded-full px-5 shadow-[0_8px_24px_-8px_rgba(255,101,0,0.7)]" onClick={() => setLead("")}>Conhecer o MapIA</Btn>
          </div>
        </div>
      </header>

      <section className="hero-mesh grain relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#FF6500]/20 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 pt-14 pb-20 sm:py-20 lg:py-28 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center relative z-10">
          <div className="rise">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-[#FF8A3D] mb-6"><span className="w-1.5 h-1.5 rounded-full bg-[#FF6500] animate-pulse" />Gerenciador inteligente de mapas de merenda escolar</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Do documento ao mapa de merenda, <span className="text-[#FF6500]">sem retrabalho.</span>
            </h1>
            <p className="text-slate-300 mt-6 text-base md:text-lg max-w-xl">Organize notas fiscais, cardápio, estoque, comensais e contratos da agricultura familiar da sua escola em um único sistema.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <Btn data-testid="hero-hire-button" className="group !rounded-full px-7 py-3.5 text-base justify-center shadow-[0_14px_40px_-10px_rgba(255,101,0,0.8)] hover:-translate-y-0.5 transition-transform" onClick={() => setLead("")}>Conhecer o MapIA <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></Btn>
              <Link to="/login" className="sm:w-auto"><Btn variant="light" data-testid="hero-access-button" className="w-full !rounded-full px-7 py-3.5 text-base justify-center hover:-translate-y-0.5 transition-transform">Acessar</Btn></Link>
            </div>
            {offer?.active && <div data-testid="hero-offer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm text-white"><Gift className="w-4 h-4 text-[#FF8A3D]" />Primeiro mapa grátis para as 100 primeiras escolas · <b className="text-[#FF8A3D]">restam {offer.remaining} vagas</b></div>}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-slate-400">
              <span className="flex items-center gap-2"><FileStack className="w-4 h-4 text-[#FF8A3D]" />PDF, imagem e XML</span>
              <span className="flex items-center gap-2"><FileDown className="w-4 h-4 text-[#FF8A3D]" />Downloads em PDF e planilha</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FF8A3D]" />Multi escolas com isolamento total de dados entre unidades</span>
            </div>
          </div>
          <div className="relative rise" style={{ animationDelay: ".15s" }}>
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#FF6500]/40 to-transparent blur-2xl" />
            <img src="/img/hero.jpg" alt="Refeição escolar" className="relative rounded-3xl w-full h-[260px] sm:h-[400px] object-cover shadow-2xl ring-1 ring-white/10" />
            <div className="absolute -bottom-6 -left-6 bg-white/95 backdrop-blur rounded-2xl shadow-xl p-4 w-64 hidden sm:block">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Saldo final = inicial + entradas − saídas</div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full w-3/4 bg-[#FF6500]" /></div>
              <div className="text-xs text-slate-500 mt-2">Recalculado a cada edição</div>
            </div>
          </div>
        </div>
      </section>

      <section id="beneficios" className="max-w-7xl mx-auto px-5 py-20 sm:py-28 scroll-mt-16">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6500]">Benefícios</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B192C] max-w-2xl mt-3">Feito para a rotina de quem fecha o mapa da escola.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {BENEFITS.map(([Icon, t, d], i) => (
            <div key={t} data-testid={`benefit-${i}`} className={`group rounded-3xl p-7 border bg-white hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(11,25,44,0.25)] transition-[transform,box-shadow] duration-300 ${i === 0 ? "lg:col-span-2 bg-gradient-to-br from-[#FFF3EB] to-white border-orange-100" : "border-slate-200"}`}>
              <div className="w-12 h-12 rounded-2xl bg-[#FF6500]/10 grid place-items-center group-hover:bg-[#FF6500] transition-colors"><Icon className="w-6 h-6 text-[#FF6500] group-hover:text-white transition-colors" /></div>
              <h3 className="font-heading text-lg font-bold text-[#0B192C] mt-4">{t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="funcionalidades" className="bg-white border-y border-slate-200 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-5 py-20 sm:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-24">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6500]">Funcionalidades</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B192C] mt-3">Funcionalidades</h2>
            <img src="/img/agro.jpg" alt="Alimentos da agricultura familiar" className="rounded-3xl w-full h-[240px] sm:h-[380px] object-cover mt-8 shadow-xl" />
          </div>
          <ul className="grid sm:grid-cols-2 gap-4">
            {FEATURES.map((f, i) => (
              <li key={f} data-testid={`feature-${i}`} className="flex gap-3 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5 text-sm text-slate-700 leading-relaxed hover:border-orange-200 hover:bg-[#FFF8F3] transition-colors"><span className="mt-0.5 w-6 h-6 rounded-full bg-[#1E3E62] grid place-items-center shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>{f}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="planos" className="max-w-7xl mx-auto px-5 py-20 sm:py-28 scroll-mt-16">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6500]">Planos</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B192C] mb-10 mt-3">Planos</h2>
        {offer?.active && <div className="mb-8 max-w-4xl mx-auto"><OfferBanner offer={offer} onHire={setLead} /></div>}
        <Plans onHire={setLead} />
      </section>

      <section id="faq" className="max-w-7xl mx-auto px-5 pb-20 sm:pb-28 grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-14 scroll-mt-16">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6500]">Perguntas</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B192C] mt-3">Perguntas frequentes</h2>
        </div>
        <Accordion type="single" collapsible className="bg-white rounded-3xl border border-slate-200 px-6 shadow-sm">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`}>
              <AccordionTrigger data-testid={`faq-item-${i}`} className="text-left font-semibold text-[#0B192C]">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-5 pb-20 sm:pb-28">
        <div className="hero-mesh grain relative overflow-hidden rounded-[2rem] px-6 sm:px-12 py-12 sm:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-[#FF6500]/25 blur-3xl pointer-events-none" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white max-w-xl relative z-10">Pronto para organizar o mapa de merenda da sua escola?</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto relative z-10">
            <Btn data-testid="cta-hire-button" className="!rounded-full px-7 py-3.5 text-base justify-center shadow-[0_14px_40px_-10px_rgba(255,101,0,0.8)]" onClick={() => setLead("")}>Conhecer o MapIA <ArrowRight className="w-4 h-4" /></Btn>
            <Link to="/login"><Btn variant="light" data-testid="cta-access-button" className="w-full !rounded-full px-7 py-3.5 text-base justify-center">Acessar</Btn></Link>
          </div>
        </div>
      </section>
      <footer className="bg-[#0B192C] text-slate-400 text-sm">
        <div className="max-w-7xl mx-auto px-5 pt-8 pb-28 flex flex-col sm:flex-row justify-between gap-3">
          <Logo light />
          <span>MapIA é um produto da WebFast Soluções Inteligentes.</span>
        </div>
      </footer>
      {lead !== null && <LeadDialog offer={offer} filling={lead} onClose={() => setLead(null)} />}
    </div>
  );
}
